import { useEffect, useMemo, useState } from 'react'
import { CaretDown, CaretRight, MagnifyingGlass } from '@phosphor-icons/react'
import { cn } from '@/lib/cn'
import { Badge, Input, type Tone } from '@/ui'
import { DECLARATION_STATE_LABEL, LEVEL_LABEL, LOCATION_ROLE_LABEL, type LocationRow } from '../api/store'
import { bayNeedsDeclaration, bayState, filterTree, visibleNodes, type BayState, type TreeNode } from '../model/locationTree'

const STATE_TONE: Record<BayState, Tone> = {
  OK: 'ok',
  EXPIRING: 'warn',
  NOT_CLEAN: 'danger',
  EXPIRED_OR_OLD_FORM: 'danger',
  NONE: 'neutral',
}

export function RoleBadge({ role }: { role: string | null }) {
  if (!role || role === 'STORAGE') return null
  const l = LOCATION_ROLE_LABEL[role]
  return (
    <span title={l?.hi}>
      <Badge tone={role === 'CONVENTIONAL' ? 'danger' : role === 'HOLD_AREA' ? 'warn' : 'neutral'}>{l?.en ?? role}</Badge>
    </span>
  )
}

/** Bay declaration state badge (bays only). */
export function BayStateBadge({ row, expiring }: { row: LocationRow; expiring: boolean }) {
  if (row.level !== 'BAY') return null
  // A red-tie bay, hold area, sample store or dock never needs a declaration.
  if (!bayNeedsDeclaration(row.role) && !row.declaration) return null
  const s = bayState(row.declaration, expiring)
  return <Badge tone={STATE_TONE[s]}>{DECLARATION_STATE_LABEL[s]}</Badge>
}

/** The site as an indented list: site → zone → bay → spot, with search and collapse. */
export function LocationTree({
  nodes,
  selectedId,
  onSelect,
  expiringBayIds,
}: {
  nodes: TreeNode[]
  selectedId: string | null
  onSelect: (id: string) => void
  expiringBayIds: ReadonlySet<string>
}) {
  const [query, setQuery] = useState('')
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())

  const shown = useMemo(() => {
    const selected = nodes.find((n) => n.row.id === selectedId)
    // A place picked from outside (label lookup) is always visible.
    const open = new Set([...collapsed].filter((id) => !selected?.ancestors.includes(id)))
    return query.trim() ? filterTree(nodes, query) : visibleNodes(nodes, open)
  }, [nodes, collapsed, query, selectedId])

  useEffect(() => {
    if (selectedId) document.getElementById(`place-${selectedId}`)?.scrollIntoView({ block: 'nearest' })
  }, [selectedId])

  const toggle = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  return (
    <div className="flex flex-col gap-3">
      <Input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Filter by name, code or label"
        aria-label="Filter places"
        leading={<MagnifyingGlass />}
        className="h-9 text-[14px]"
      />
      {shown.length === 0 ? (
        <p className="py-6 text-center text-[14px] text-muted">No place matches “{query.trim()}”.</p>
      ) : (
        <div className="-mx-4 overflow-x-auto">
          <table className="w-full min-w-[34rem] text-left text-[14px]">
            <caption className="sr-only">Places at this site</caption>
            <thead>
              <tr className="border-b border-rule font-mono text-[10.5px] font-bold uppercase tracking-label text-muted">
                <th className="px-4 py-2 font-bold">Place</th>
                <th className="hidden px-2 py-2 font-bold sm:table-cell">Code</th>
                <th className="px-2 py-2 text-right font-bold">Items</th>
                <th className="px-4 py-2 font-bold">State</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((n) => {
                const r = n.row
                const isOpen = !collapsed.has(r.id) || Boolean(query.trim())
                const selected = r.id === selectedId
                return (
                  <tr
                    key={r.id}
                    id={`place-${r.id}`}
                    className={cn(
                      'border-b border-rule/60 last:border-0',
                      selected ? 'bg-amber-wash' : 'hover:bg-paper-tint',
                      !r.active && 'text-muted',
                    )}
                  >
                    <td className="py-1.5 pr-2" style={{ paddingLeft: `${1 + n.depth * 1.25}rem` }}>
                      <div className="flex items-center gap-1">
                        {n.childCount > 0 && !query.trim() ? (
                          <button
                            type="button"
                            onClick={() => toggle(r.id)}
                            aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${r.name}`}
                            aria-expanded={isOpen}
                            className="grid h-6 w-6 shrink-0 place-items-center rounded-chip text-muted hover:text-ink"
                          >
                            {isOpen ? <CaretDown aria-hidden="true" /> : <CaretRight aria-hidden="true" />}
                          </button>
                        ) : (
                          <span className="w-6 shrink-0" aria-hidden="true" />
                        )}
                        <button
                          type="button"
                          onClick={() => onSelect(r.id)}
                          aria-current={selected ? 'true' : undefined}
                          className="flex min-w-0 flex-col items-start text-left focus-visible:underline"
                        >
                          <span className={cn('truncate', r.level === 'SITE' || r.level === 'ZONE' ? 'font-semibold text-ink' : 'text-ink', !r.active && 'line-through text-muted')}>
                            {r.name}
                          </span>
                          <span className="font-mono text-[10.5px] uppercase tracking-label text-muted">
                            {LEVEL_LABEL[r.level] ?? r.level}
                            <span className="sm:hidden"> · {r.code}</span>
                          </span>
                        </button>
                      </div>
                    </td>
                    <td className="hidden px-2 py-1.5 font-mono text-[13px] sm:table-cell">{r.code}</td>
                    <td className="px-2 py-1.5 text-right font-mono tabular">
                      <span className={n.itemsBelow ? 'text-ink' : 'text-muted'}>{n.itemsBelow}</span>
                    </td>
                    <td className="px-4 py-1.5">
                      <div className="flex flex-wrap gap-1">
                        <RoleBadge role={r.role} />
                        <BayStateBadge row={r} expiring={expiringBayIds.has(r.id)} />
                        {!r.active && <Badge tone="neutral">Not in use</Badge>}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
